
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import FingerprintRoundedIcon from "@mui/icons-material/FingerprintRounded";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";

import { APP_FIELD_SX, APP_UI } from "../ui/appUi";
import { AUTH_VALIDATION, boundedTextError } from "../utils/apiValidation";
import { useSequentialPolling } from "../hooks/useSequentialPolling";
import AppDialogSurface from "../components/AppDialogSurface";
import { loginWithPasskey, passkeysSupported } from "../services/passkeyService";

import {
  checkServerConnection,
  loginUser,
  type AuthUser,
  type ServerConnectionState,
} from "../services/authService";

interface LoginPageProps {
  onLogin: (user: AuthUser) => void;
}

function connectionLabel(state: ServerConnectionState): string {
  if (state === "checking") return "Server wird geprüft";
  if (state === "online") return "Server erreichbar";
  if (state === "slow") return "Server antwortet langsam";
  return "Server nicht erreichbar";
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] =
    useState<ServerConnectionState>("checking");
  const [forgotOpen, setForgotOpen] = useState(false);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  useSequentialPolling({
    intervalMs: 15_000,
    initialDelayMs: 0,
    pauseWhenHidden: true,
    request: () => checkServerConnection(),
    onSuccess: setConnection,
    onError: () => setConnection("offline"),
  });

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;

    const normalizedUsername = username.trim();
    const usernameError = boundedTextError("Benutzername", normalizedUsername, AUTH_VALIDATION.login_username);
    const passwordError = boundedTextError("Passwort", password, AUTH_VALIDATION.password_input, false);
    if (usernameError || passwordError) {
      setError(usernameError ?? passwordError ?? "Bitte Benutzername und Passwort eingeben.");
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const user = await loginUser(normalizedUsername, password);
      setConnection("online");
      onLogin(user);
    } catch (unknownError) {
      const state = await checkServerConnection();
      setConnection(state);
      setError(
        state === "offline"
          ? "Anmeldung nicht möglich, da die Netzwerkverbindung zum Server nicht verfügbar ist."
          : unknownError instanceof Error
            ? unknownError.message
            : "Anmeldung fehlgeschlagen."
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitPasskey(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const user = await loginWithPasskey();
      setConnection("online");
      onLogin(user);
    } catch (unknownError) {
      setError(unknownError instanceof Error ? unknownError.message : "Passkey-Anmeldung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "transparent",
        color: "#f5f5f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pt: "max(1rem, env(safe-area-inset-top))",
        pb: "max(1rem, env(safe-area-inset-bottom))",
      }}
    >
      <Box
        component="form"
        onSubmit={(event) => void submit(event)}
        sx={{
          width: "100%",
          maxWidth: 430,
        }}
      >
        <Stack spacing={1.6}>
          <Box sx={{ textAlign: "center", mb: 1.4 }}>
            <Box
              component="img"
              src="/icons/materialentnahme-logo-512.png"
              alt="Materialentnahme Logo"
              sx={{
                display: "block",
                width: 126,
                height: 126,
                objectFit: "contain",
                mx: "auto",
                mb: 0.8,
                filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.30))",
                "@media (max-height: 700px)": {
                  width: 102,
                  height: 102,
                },
              }}
            />

            <Typography
              variant="h4"
              sx={{
                color: "#d90039",
                fontWeight: 900,
                letterSpacing: "-0.025em",
                lineHeight: 1.05,
              }}
            >
              Materialentnahme
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#9b9ca2", mt: 0.7 }}
            >
              Mit Ihrem Benutzerkonto anmelden
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2.7,
                bgcolor: "rgba(211,47,47,0.10)",
              }}
            >
              {error}
            </Alert>
          )}

          <TextField
            label="Benutzername"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            fullWidth
            disabled={busy}
            slotProps={{
              htmlInput: {
                enterKeyHint: "next",
                onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    passwordRef.current?.focus();
                  }
                },
              },
            }}
            sx={APP_FIELD_SX}
          />

          <TextField
            label="Passwort"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            fullWidth
            disabled={busy}
            inputRef={passwordRef}
            slotProps={{
              htmlInput: { enterKeyHint: "go" },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Passwort ausblenden" : "Passwort anzeigen"}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setShowPassword((current) => !current)}
                      edge="end"
                      disabled={busy}
                      sx={{ color: "rgba(255,255,255,0.82)" }}
                    >
                      {showPassword
                        ? <VisibilityOffOutlinedIcon />
                        : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            sx={APP_FIELD_SX}
          />

          <Box sx={{ textAlign: "right", mt: -0.4 }}>
            <Button
              type="button"
              variant="text"
              onClick={() => setForgotOpen(true)}
              sx={{
                minHeight: 40,
                px: 0.6,
                color: "#83cee0",
                textTransform: "none",
                fontWeight: 750,
              }}
            >
              Passwort vergessen?
            </Button>
          </Box>

          <Button
            type="submit"
            variant="contained"
            disabled={busy || connection === "offline"}
            startIcon={
              busy
                ? <CircularProgress size={18} color="inherit" />
                : <LockOutlinedIcon />
            }
            sx={{
              minHeight: 52,
              borderRadius: 999,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 850,
              bgcolor: "#83cee0",
              color: "#0a3139",
              boxShadow: "none",
              "&:hover": { bgcolor: "#91d8e8", boxShadow: "none" },
            }}
          >
            {busy ? "Anmeldung läuft …" : "Anmelden"}
          </Button>

          {passkeysSupported() && (
            <Button
              type="button"
              variant="outlined"
              disabled={busy || connection === "offline"}
              startIcon={<FingerprintRoundedIcon />}
              onClick={() => { void submitPasskey(); }}
              sx={{ minHeight: 48, borderRadius: 999, textTransform: "none", fontWeight: 850, color: APP_UI.accent, borderColor: APP_UI.accentBorder }}
            >
              Mit Fingerabdruck / Face ID anmelden
            </Button>
          )}

          <Stack
            direction="row"
            spacing={0.8}
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minHeight: 28,
              pt: 0.3,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                flex: "0 0 auto",
                borderRadius: "50%",
                bgcolor:
                  connection === "online"
                    ? "#69d27d"
                    : connection === "slow"
                      ? APP_UI.warning
                      : connection === "offline"
                        ? "#ef5350"
                        : "#85878d",
                boxShadow:
                  connection === "online"
                    ? "0 0 8px rgba(105,210,125,0.42)"
                    : connection === "slow"
                      ? "0 0 8px rgba(244,163,64,0.34)"
                      : connection === "offline"
                        ? "0 0 8px rgba(239,83,80,0.34)"
                        : "none",
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color:
                  connection === "offline" ? "#ef9a9a" : connection === "slow" ? APP_UI.warning : "#96989e",
                fontWeight: 700,
              }}
            >
              {connectionLabel(connection)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      <AppDialogSurface
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title="Passwort vergessen?"
      >
        <Typography variant="body2" sx={{ color: "#c3c4c8" }}>
          Das Passwort kann durch einen Administrator zurückgesetzt werden. Bitte wenden Sie sich an einen Administrator.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setForgotOpen(false)}
          sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", color: APP_UI.accent, borderColor: APP_UI.accentBorder, fontWeight: 800 }}
        >
          Verstanden
        </Button>
      </AppDialogSurface>
    </Box>
  );
}
